(ns ui.view.entity-graph.export
  (:require [re-frame.core :as rf]
            [common.localization :as loc]
            [common.schema.registry :as registry]
            [frontend.exports.excel :as excel]
            [frontend.state.current-session :as session]
            [frontend.state.permissions :as permissions]
            [frontend.utils :as fu]
            [ui.util :as uu]
            [ui.view.entity-graph.model :as m]
            [ui.view.entity-graph.utils :as utils]))

;; required functionality won't directly map
;; to our field-schema definitions (e.g. headers will differ
;; from attribute labels based on graph-ref type)
;; => using graph-ref specific attribute definitions + corresponding
;;    display-graph-ref function for displaying these
(def ref-attributes
  {:graph-reference.type/ownership
   [{:key :graph-reference/source
     :label "Owner (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Owner (name)"}
    {:key [:graph-reference/source :graph-entity/entity-type]
     :tr? true
     :label "Type"}
    {:key [:metadata :graph-meta/ownership-percentage]
     :field/data-type :data.type/percentage
     :label "Percentage"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "In (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}
    {:key [:graph-reference/target :graph-entity/entity-type]
     :tr? true
     :label "Type"}]
   :graph-reference.type/gp
   [{:key :graph-reference/source
     :label "GP (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "GP (name)"}
    {:key [:graph-reference/source :graph-entity/entity-type]
     :tr? true
     :label "Type"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "In fund (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}
    {:key [:graph-reference/target :graph-entity/entity-type]
     :tr? true
     ;; Our fund or Third party fund
     :label "Type"}]
   :graph-reference.type/lp
   [{:key :graph-reference/source
     :label "LP (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "LP (name)"}
    {:key [:graph-reference/source :graph-entity/entity-type]
     :tr? true
     :label "Type"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "In fund (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}
    {:key [:graph-reference/target :graph-entity/entity-type]
     :tr? true
     ;; Our fund or Third party fund
     :label "Type"}]
   :graph-reference.type/board-member
   [{:key :graph-reference/source
     :label "Board member (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Board member (name)"}
    {:key [:metadata :graph-meta/board-member-role]
     :field/data-type :data.type/keyword
     :label "Role"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Company"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}]
   :graph-reference.type/executive
   [{:key :graph-reference/source
     :label "Executive (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Executive (name)"}
    {:key [:metadata :graph-meta/executive-role]
     :field/data-type :data.type/keyword
     :label "Role"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Company"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}]
   :graph-reference.type/auditor
   [{:key :graph-reference/source
     :label "Auditor (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Auditor (name)"}
    {:key [:metadata :graph-meta/auditor-role]
     :field/data-type :data.type/keyword
     :label "Role"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Company"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}]
   :graph-reference.type/auditing-firm
   [{:key :graph-reference/source
     :label "Auditing firm (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Auditing firm (name)"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Company"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}]
   :graph-reference.type/management-company
   [{:key :graph-reference/source
     :label "ManCo (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "ManCo (name)"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Of fund (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}
    {:key [:graph-reference/target :graph-entity/entity-type]
     :tr? true
     ;; Our fund or Third party fund
     :label "Type"}]
   :graph-reference.type/aifm
   [{:key :graph-reference/source
     :label "AIFM (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "AIFM (name)"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Of (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}
    {:key [:graph-reference/target :graph-entity/entity-type]
     :tr? true
     ;; Company / Our fund / Third party fund
     :label "Type"}]
   :graph-reference.type/advisor
   [{:key :graph-reference/source
     :label "Advisor (Map-ID)"}
    {:key [:graph-reference/source :graph-entity/label]
     :label "Advisor (name)"}
    {:key [:graph-reference/target :graph-entity/label]
     :label "Of company (name)"}
    {:key [:graph-reference/target :db/id]
     :label "Map-ID"}]})

(defn display-data
  [{:keys [session-entities
           tenant-schema
           entity-schema]} entity attribute]
  (let [fs (get entity-schema attribute)
        raw-value (get entity attribute)]
    (fu/display-data
     tenant-schema @session/tenant attribute fs
     entity
     :passed-value raw-value
     :entities session-entities)))

(defn display-graph-ref
  [{:keys [_session-entities
           entities
           _tenant-schema
           _entity-schema]}
   graph-ref
   attr-def]
  (let [attribute (:key attr-def)
        tr? (:tr? attr-def)
        e-attribute? (and (vector? attribute)
                          (or (= (first attribute)
                                 :graph-reference/source)
                              (= (first attribute)
                                 :graph-reference/target)))
        e-entity (when e-attribute?
                   (-> attribute
                       first
                       (->> (get graph-ref)
                            (get entities))))
        meta? (and (vector? attribute)
                   (= (first attribute) :metadata))
        meta-e (when meta?
                 (->> (:graph-reference/metadata graph-ref)
                      (map (fn [id]
                             (get entities id)))
                      (filter (fn [m]
                                (contains? m (second attribute))))
                      first))
        data-type (:field/data-type attr-def)
        meta-keyword? (and meta?
                           (= data-type :data.type/keyword))
        meta-options (when meta-keyword?
                       (:options (registry/get-field-schema
                                  nil
                                  (second attribute))))
        percentage? (= data-type :data.type/percentage)
        raw (cond (keyword? attribute)
                  (get graph-ref attribute)

                  e-attribute?
                  (get e-entity (second attribute))

                  meta?
                  (get meta-e (second attribute)))]
    (cond tr?
          (loc/tr raw)

          percentage?
          (str (some-> raw
                       (* 100)
                       (fu/format-with-separators 2 false 2))
               (when raw "%"))

          meta-keyword?
          (uu/find-option-label meta-options raw)

          :else
          raw)))

(defn graph-ref-row
  [{:keys [attr-defs] :as args} graph-reference]
  (->> attr-defs
       (mapv (partial display-graph-ref args graph-reference))))

(defn graph-ref-type-sheet-data
  [{:keys [] :as args} [ref-type graph-refs]]
  (let [attr-defs (get ref-attributes ref-type)
        header-row (->> attr-defs (mapv :label))
        title (loc/tr ref-type)
        rows (->> graph-refs
                  (mapv (partial graph-ref-row (assoc args
                                                      :attr-defs attr-defs))))
        all-rows (into [header-row] rows)]
    (into [[title]] all-rows)))

(defn graph-ref-data
  [{:keys [visible-connectors
           other-graph-references] :as args} {:keys [] :as _ctx}]
  (let [connectors-by-type (->> visible-connectors
                                (group-by :graph-reference/type))
        connector-sheets-data (->> connectors-by-type
                                   (mapv (partial graph-ref-type-sheet-data args)))
        others-by-type (->> other-graph-references
                            (group-by :graph-reference/type))
        other-sheets-data (->> others-by-type
                               (mapv (partial graph-ref-type-sheet-data args)))
        result (into connector-sheets-data other-sheets-data)]
    result))

(defn export-attributes
  [{:keys [entity-schema entity-type] :as _args}]
  (let [always [:db/id :graph-entity/label :graph-entity/entity]
        always-set (set always)
        fields (->> entity-schema
                    (filterv (fn [[f _]]
                               (not (contains? always-set f))))
                    (filterv (fn [[f _]]
                               ;; OR remove :editor.type/none
                               (not (contains? #{:graph-entity/entity-type} f))))
                    (filterv (fn [[f _]]
                               (let [read? (permissions/field-read-access?
                                            f entity-type nil)]
                                 read?)))
                    (filterv (fn [[a _]]
                               (= (namespace a)
                                  "graph-entity")))
                    (sort-by (fn [[a _]]
                               (utils/attr-order a)))
                    (mapv first))
        attributes (into always fields)]
    attributes))

(defn map-profile-row-data
  [{:keys [attributes] :as args} graph-entity]
  (let [entity-rows (->> attributes
                         (mapv (partial display-data args graph-entity)))]
    entity-rows))

;; One sheet data for graph-entity-type (e.g. :entity.type/graph-company)
(defn map-profile-type-data
  [{:keys [visible-map-profiles _visible-connectors
           hidden-needed-map-profiles
           tenant-schema] :as args} _ctx entity-type]
  (let [entity-schema (registry/get-schema tenant-schema entity-type)
        args (assoc args
                    :entity-schema entity-schema
                    :entity-type entity-type)
        attributes (export-attributes args)
        args (assoc args :attributes attributes)
        header-row (into
                    ["Visible in graph"]
                    (->> attributes
                         (mapv (fn [a]
                                 (if (= a :db/id)
                                   "Map-ID"
                                   (loc/tr a))))))
        visible-map-profiles (->> visible-map-profiles
                                  (filterv (fn [{:keys [entity/type]}]
                                             (= type entity-type)))
                                  (mapv (fn [entity]
                                          (into [true]
                                                (map-profile-row-data args entity)))))
        hidden-map-profiles (->> hidden-needed-map-profiles
                                 (filterv (fn [{:keys [entity/type]}]
                                            (= type entity-type)))
                                 (mapv (fn [entity]
                                         (into [false]
                                               (map-profile-row-data args entity)))))
        entities-of-type (into visible-map-profiles hidden-map-profiles)]
    (when (seq entities-of-type)
      (into [header-row]
            entities-of-type))))

(defn map-profile-data
  [args ctx]
  (let [company-sheet (map-profile-type-data args ctx :entity.type/graph-company)
        person-sheet (map-profile-type-data args ctx :entity.type/graph-person)
        fof-sheet (map-profile-type-data args ctx :entity.type/graph-fof-asset)
        fund-sheet (map-profile-type-data args ctx :entity.type/graph-fund)
        result (cond-> []
                 company-sheet (conj (into [["Companies"]] company-sheet))
                 person-sheet (conj (into [["Contacts"]] person-sheet))
                 fof-sheet (conj (into [["Third party funds"]] fof-sheet))
                 fund-sheet (conj (into [["Funds"]] fund-sheet)))]
    result))

(defn get-sheet-data
  [{:keys [db entities session-entities tenant-schema] :as cofx} ctx]
  (let [data (utils/get-visible-data cofx ctx)
        map-profile-sheets (map-profile-data
                            (assoc data
                                   :db db
                                   :tenant-schema tenant-schema
                                   :entities entities
                                   :session-entities session-entities)
                            ctx)
        graph-ref-sheets (graph-ref-data
                          (assoc data
                                 :db db
                                 :tenant-schema tenant-schema
                                 :entities entities
                                 :session-entities session-entities)
                          ctx)
        data-sheets (into map-profile-sheets
                          graph-ref-sheets)]
    data-sheets))

(rf/reg-fx
 m/export-as-excel-fx
 (fn [{:keys [filename sheets-data]}]
   (excel/export-multiple-sheet-array-data filename sheets-data)))
